import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearNotifications,
  selectCustomFieldResponsesNotifications,
} from "../reducers/customFieldResponsesSlice";
import { useNotificationBanner, getSaveBannerActions } from "./notificationHooks";

export const useInlineResponseEditing = (onLeave) => {
  const dispatch = useDispatch();
  const notifications = useSelector(selectCustomFieldResponsesNotifications);

  const [editingKey, setEditingKey] = useState(null);
  const commitRef = useRef(() => {});
  const leaveRef = useRef(() => {});
  leaveRef.current = onLeave ?? (() => {});
  const leaveRequestedRef = useRef(false);

  const stopEditing = useCallback(() => {
    commitRef.current = () => {};
    setEditingKey(null);
  }, []);

  const requestEdit = (key) => {
    if (key === null) {
      stopEditing();
      return;
    }
    if (commitRef.current?.()) {
      return;
    }
    commitRef.current = () => {};
    setEditingKey(key);
  };

  useEffect(() => {
    if (!editingKey) {
      return undefined;
    }
    const handleOutside = (event) => {
      if (event.target.closest?.("[data-editing-answer='true']")) {
        return;
      }
      if (commitRef.current?.()) {
        return;
      }
      stopEditing();
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [editingKey, stopEditing]);

  const pendingEditsRef = useRef(new Map());
  const bannerDiscardRef = useRef(() => {});

  const saveAll = async () => {
    const pending = [...pendingEditsRef.current.values()];
    await Promise.all(pending.map((edit) => edit.save?.()));
  };

  const [banner, setUnsaved] = useNotificationBanner({
    notifications,
    timeoutAction: () => dispatch(clearNotifications()),
    bannerActions: getSaveBannerActions(saveAll, () => bannerDiscardRef.current()),
  });

  const setPendingEdit = (key, descriptor) => {
    if (descriptor) {
      pendingEditsRef.current.set(key, descriptor);
    } else {
      pendingEditsRef.current.delete(key);
    }
    if (pendingEditsRef.current.size === 0) {
      leaveRequestedRef.current = false;
    }
    setUnsaved(pendingEditsRef.current.size > 0);
  };

  const discardAll = () => {
    pendingEditsRef.current.forEach((edit) => edit.discard?.());
    pendingEditsRef.current.clear();
    leaveRequestedRef.current = false;
    setUnsaved(false);
  };

  bannerDiscardRef.current = () => {
    const shouldLeave = leaveRequestedRef.current;
    discardAll();
    if (shouldLeave) {
      leaveRef.current();
    }
  };

  const leftNavigation = () => {
    commitRef.current?.();
    if (pendingEditsRef.current.size > 0 && !leaveRequestedRef.current) {
      leaveRequestedRef.current = true;
      setUnsaved(true);
      return;
    }
    discardAll();
    leaveRef.current();
  };

  return {
    banner,
    editingKey,
    requestEdit,
    commitRef,
    setPendingEdit,
    leftNavigation,
  };
};
